import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, CloseIcon, Dialog, Flex, Text, TextArea, } from "@fluentui/react-northstar";
import { StarFilled, StarRegular } from "@fluentui/react-icons";
import { memo, useCallback, useMemo } from "react";
import { useReviewDialog } from "./ReviewDialog.logic";
import { Modal } from "../../../interfaces/Modal";
import { useMsTeamsSelector } from "../../../redux/reducers/MicrosoftTeamsReducer/MicrosoftTeamsReducer";
const STARS_COUNT = 5;
export const ReviewDialog = memo((props) => {
    const logic = useReviewDialog(props);
    const { isOnMobile } = useMsTeamsSelector("isOnMobile");
    const onStarClick = useCallback((starIndex) => (e) => {
        const value = starIndex;
        logic.onRate(value);
    }, [logic]);
    const onStarMouseEnter = useCallback((value) => () => logic.onStarHover(value), [logic]);
    const onStarMouseLeave = useCallback(() => logic.onStarHover(0), [logic]);
    const dialogContent = useMemo(() => {
        if (logic.step === "rating") {
            return (_jsxs(Flex, { column: true, gap: "gap.medium", style: { padding: "1.5rem 0" }, children: [_jsx(Flex, { column: true, gap: "gap.small", children: _jsx(Text, { content: logic.t("ReviewSubtitleShare"), size: "medium" }) }), _jsx(Flex, { vAlign: "center", hAlign: "center", gap: "gap.small", styles: { marginTop: "0.5rem" }, className: "enable-margin", children: Array.from({ length: STARS_COUNT }, (_, i) => i + 1).map((value) => {
                            const displayRating = logic.hoverRating || logic.rating;
                            const isFilled = displayRating >= value;
                            const StarSvg = isFilled ? StarFilled : StarRegular;
                            return (_jsx(Button, { icon: _jsx(StarSvg, { style: { width: isOnMobile ? "16px" : "32px", height: isOnMobile ? "16px" : "32px" } }), text: true, primary: isFilled, onClick: onStarClick(value), onMouseEnter: onStarMouseEnter(value), onMouseLeave: onStarMouseLeave, title: `${value} ${value === 1 ? "star" : "stars"}`, className: "no-border", styles: {
                                    minWidth: "1rem",
                                    height: "2rem",
                                    padding: 0,
                                    "& div": {
                                        width: isOnMobile ? "16px" : "32px",
                                        height: isOnMobile ? "16px" : "32px",
                                    },
                                } }, value));
                        }) }), _jsx(Flex, { vAlign: "center", hAlign: "center", fill: true, children: _jsx(Text, { content: logic.t("ReviewQuote"), size: "medium", style: {
                                color: "#605e5c",
                                fontSize: "0.875rem",
                                marginTop: "0.25rem",
                                fontStyle: "italic",
                            } }) })] }));
        }
        if (logic.step === "feedback") {
            const filledCount = Math.floor(logic.rating);
            return (_jsxs(Flex, { column: true, gap: "gap.medium", style: { padding: "1.5rem 0" }, children: [_jsx(Flex, { column: true, gap: "gap.small", className: "enable-margin", children: _jsx(Text, { content: logic.t("ReviewFeedbackQuestion"), size: "medium" }) }), _jsx(Flex, { vAlign: "center", hAlign: "center", gap: "gap.small", styles: { marginTop: "0rem", marginBottom: "0rem !important" }, children: Array.from({ length: STARS_COUNT }, (_, i) => i + 1).map((value) => {
                            const isFilled = value <= filledCount;
                            const StarSvg = isFilled ? StarFilled : StarRegular;
                            return (_jsx(Button, { icon: _jsx(StarSvg, { "aria-disabled": true, style: { width: isOnMobile ? "16px" : "32px", height: isOnMobile ? "16px" : "32px"
                                    } }), text: true, primary: isFilled, "aria-disabled": true, className: "no-border", styles: {
                                    minWidth: "1rem",
                                    height: "2rem",
                                    padding: 0,
                                    "& div": {
                                        width: isOnMobile ? "16px" : "32px",
                                        height: isOnMobile ? "16px" : "32px",
                                    },
                                    "&:hover": {
                                        color: isFilled ? "" : "rgb(200, 198, 196);",
                                    },
                                    cursor: "default"
                                } }, value));
                        }) }), _jsx(TextArea, { fluid: true, placeholder: logic.t("WhatCouldWeImprove"), value: logic.comment, onChange: (_, data) => logic.setComment(data?.value ?? ""), styles: { minHeight: "100px", marginTop: "0.9375rem" }, maxLength: 1000, disabled: logic.isSubmittingFeedback }), _jsxs(Flex, { column: true, gap: "gap.small", vAlign: "center", hAlign: "center", fill: true, children: [_jsx(Button, { type: "button", primary: true, content: logic.t("SubmitMyFeedback"), onClick: logic.onSubmitFeedback, fluid: true, loading: logic.isSubmittingFeedback, disabled: logic.isSubmittingFeedback }), _jsx(Button, { as: "a", href: logic.feedbackFormUrl, target: "_blank", rel: "noopener noreferrer", content: logic.t("FillCompleteFeedbackForm"), text: true, styles: { color: "#6264a7", marginTop: "0.25rem", pointerEvents: logic.isSubmittingFeedback ? "none" : undefined, opacity: logic.isSubmittingFeedback ? 0.6 : undefined } })] })] }));
        }
        if (logic.step === "appsource") {
            if (logic.appSourceReviewUrl) {
                return (_jsx(Flex, { column: true, gap: "gap.medium", style: { padding: "0", minHeight: "400px" }, children: _jsx("iframe", { title: logic.t("ReviewOnAppSource"), src: logic.appSourceReviewUrl, style: {
                            width: "100%",
                            minHeight: "450px",
                            border: "none",
                            borderRadius: "4px",
                        } }) }));
            }
            return (_jsxs(Flex, { column: true, gap: "gap.medium", style: { padding: "1.5rem 0" }, vAlign: "center", hAlign: "center", children: [_jsx(Text, { content: logic.t("ReviewOnAppSource") }), _jsx(Button, { primary: true, content: logic.t("Close"), onClick: logic.onClose })] }));
        }
        return null;
    }, [
        logic.step,
        logic.rating,
        logic.comment,
        logic.t,
        logic.setComment,
        logic.onSubmitFeedback,
        logic.feedbackFormUrl,
        logic.appSourceReviewUrl,
        logic.isSubmittingFeedback,
        onStarClick,
        onStarMouseEnter,
        onStarMouseLeave,
        logic.hoverRating,
        logic.rating,
        isOnMobile
    ]);
    return (_jsx(Dialog, { header: logic.step === "rating" ? "💜 " + logic.t("ReviewTitleJoin") : "🎯 " + logic.t("ThanksForYourRating"), headerAction: {
            icon: _jsx(CloseIcon, {}),
            title: logic.t("Close"),
            onClick: logic.isSubmittingFeedback ? undefined : logic.onClose,
            disabled: logic.isSubmittingFeedback,
        }, open: logic.isOpen === Modal.REVIEW, content: dialogContent, styles: {
            width: logic.step === "appsource" ? "min(95vw, 560px)" : "min(90vw, 420px)",
            borderRadius: "8px",
            border: "1px solid #edebe9",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
        }, onCancel: logic.isSubmittingFeedback ? undefined : logic.onClose, closeOnOutsideClick: !logic.isSubmittingFeedback }));
});
